import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Stack,
  Chip,
  Paper,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import DevIndexAPIs from '../../utilities/api/devIndexAPIs';
import CustomPagination from '../CustomPagination/CustomPagination';

// TokenInput component for tokens/chips input
const TokenInput = ({
  tokens = [],
  setTokens,
  placeholder = 'Add value',
  disabled = false,
  single = false,
}) => {
  const [input, setInput] = useState('');
  const tokensArray = Array.isArray(tokens) ? tokens : [];

  const safeSetTokens = newTokens => {
    if (disabled) return;
    if (typeof setTokens === 'function') {
      setTokens(newTokens);
    }
  };

  const addToken = value => {
    const v = (value || '').trim();
    if (!v || disabled) return;
    if (single) {
      safeSetTokens([v]);
      setInput('');
      return;
    }
    if (tokensArray.includes(v)) {
      setInput('');
      return;
    }
    safeSetTokens([...tokensArray, v]);
    setInput('');
  };

  const handleKeyDown = e => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addToken(input);
    }
  };

  const handlePaste = e => {
    if (disabled) return;
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const parts = paste
      .split(/[,;\n\r]+/)
      .map(p => p.trim())
      .filter(Boolean);
    if (!parts.length) return;
    if (single) {
      safeSetTokens([parts[0]]);
      return;
    }
    const uniqueParts = parts.filter(p => !tokensArray.includes(p));
    if (uniqueParts.length) safeSetTokens([...tokensArray, ...uniqueParts]);
  };

  const removeToken = index => {
    if (disabled) return;
    if (single) {
      safeSetTokens([]);
    } else {
      safeSetTokens(tokensArray.filter((_, i) => i !== index));
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          minHeight: 40,
        }}
      >
        {tokensArray.map((token, i) => (
          <Chip
            key={i}
            label={token}
            size="small"
            onDelete={disabled ? undefined : () => removeToken(i)}
            color="primary"
            variant="outlined"
            sx={{ m: 0.25, maxWidth: '200px' }}
          />
        ))}
      </Box>
      {!disabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Typography
            variant="div"
            color="text.secondary"
            sx={{ userSelect: 'none', fontSize: '0.8rem' }}
          >
            {single ? '(Single value)' : '(Press Enter or comma to add)'}
          </Typography>
          <input
            type="text"
            value={input}
            placeholder={placeholder}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            style={{
              minWidth: 120,
              marginTop: tokensArray.length ? 8 : 0,
              height: 32,
              padding: '4px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const FRTManagementContainer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector(state => state.user.user);
  const [devIndexData, setDevIndexData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingStates, setEditingStates] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const fetchDevIndexData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await DevIndexAPIs.get_dev_index();
      let sortedList = data.sort((a, b) => a.id - b.id);
      if (user.user?.TL_name !== 'MBF Admin') {
        sortedList = sortedList.filter(
          i => i.name?.toLowerCase() === user.user?.TL_name?.toLowerCase()
        );
      }
      setDevIndexData(sortedList);
      setError('');
    } catch (error) {
      setError('Failed to fetch FRT Management data');
      console.error('Error fetching FRT Management data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.user?.TL_name]);

  // Fetch data on component mount
  useEffect(() => {
    fetchDevIndexData();
  }, [fetchDevIndexData]);

  // Initialize editing states when data changes
  useEffect(() => {
    const initialStates = {};
    devIndexData.forEach(item => {
      initialStates[item.id] = {
        isEditing: false,
        tempLdaps: [...(item.accepted_ldaps || [])],
        tempDevIndexes: [...(item.devIndexes || [])],
        tempTlName: item.name ? [item.name] : [],
      };
    });
    setEditingStates(initialStates);
  }, [devIndexData]);

  const updateEditingState = useCallback((id, updates) => {
    setEditingStates(prev => ({
      ...prev,
      [id]: { ...prev[id], ...updates },
    }));
  }, []);

  const handleEdit = useCallback(id => {
    setDevIndexData(data => {
      const item = data.find(item => item.id === id);
      if (!item) return data;
      setEditingStates(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          isEditing: true,
          tempLdaps: [...(item.accepted_ldaps || [])],
          tempDevIndexes: [...(item.devIndexes || [])],
        },
      }));
      return data;
    });
  }, []);

  const handleCancel = useCallback(id => {
    setDevIndexData(data => {
      const item = data.find(item => item.id === id);
      if (!item) return data;
      setEditingStates(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          isEditing: false,
          tempLdaps: [...(item.accepted_ldaps || [])],
          tempDevIndexes: [...(item.devIndexes || [])],
          tempTlName: item.name ? [item.name] : [],
        },
      }));
      return data;
    });
  }, []);

  const handleSave = useCallback(async id => {
    setDevIndexData(currentData => {
      const item = currentData.find(item => item.id === id);
      if (!item) return currentData;

      setEditingStates(currentStates => {
        const state = currentStates[id];
        if (!state) return currentStates;

        const payload = {
          name:
            state.tempTlName && state.tempTlName[0]
              ? state.tempTlName[0]
              : item.name || '',
          accepted_ldaps: Array.isArray(state.tempLdaps)
            ? state.tempLdaps
            : item.accepted_ldaps || [],
          devIndexes: Array.isArray(state.tempDevIndexes)
            ? state.tempDevIndexes
            : item.devIndexes || [],
        };

        DevIndexAPIs.update_dev_index(id, payload)
          .then(response => {
            if (response && response.status >= 200 && response.status < 300) {
              const updatedItem =
                response.data.data && typeof response.data.data === 'object'
                  ? response.data.data
                  : payload;
              setDevIndexData(prev =>
                prev.map(it => (it.id === id ? { ...it, ...updatedItem } : it))
              );
              setSuccessMessage('Changes saved successfully!');
              setEditingStates(prev => ({
                ...prev,
                [id]: {
                  ...prev[id],
                  isEditing: false,
                  tempLdaps: [],
                  tempDevIndexes: [],
                  tempTlName: [],
                },
              }));
            } else {
              const errMsg =
                response?.data?.error ||
                response?.data?.message ||
                response?.statusText ||
                'Update failed';
              setError(errMsg);
            }
          })
          .catch(error => {
            let msg = 'Failed to save changes';
            if (error?.response) {
              msg =
                error.response.data?.error ||
                error.response.data?.message ||
                `Server error: ${error.response.status}`;
            } else if (error?.request) {
              msg = 'Network error. Please check your connection.';
            } else if (error?.message) {
              msg = error.message;
            }
            setError(msg);
            console.error('Error saving changes:', error);
          });

        return currentStates;
      });
      return currentData;
    });
  }, []);

  // Define columns for DataGrid - memoized to prevent recreating on every render
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1,
        sortable: true,
      },
      {
        field: 'name',
        headerName: 'TL Name',
        flex: 3,
        renderCell: params => {
          const state = editingStates[params.row.id] || {};
          return state.isEditing ? (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <TokenInput
                tokens={state.tempTlName || []}
                setTokens={tokens =>
                  updateEditingState(params.row.id, { tempTlName: tokens })
                }
                placeholder="Enter TL Name"
                single={true}
              />
            </Box>
          ) : (
            <TokenInput
              tokens={params.row.name ? [params.row.name] : []}
              placeholder=""
              disabled={true}
            />
          );
        },
      },
      {
        field: 'accepted_ldaps',
        headerName: 'LDAPs',
        flex: 3,
        renderCell: params => {
          const state = editingStates[params.row.id] || {};
          return state.isEditing ? (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <TokenInput
                tokens={state.tempLdaps || []}
                setTokens={tokens =>
                  updateEditingState(params.row.id, { tempLdaps: tokens })
                }
                placeholder="Enter LDAP"
              />
            </Box>
          ) : (
            <TokenInput
              tokens={params.row.accepted_ldaps || []}
              placeholder=""
              disabled={true}
            />
          );
        },
      },
      {
        field: 'devIndexes',
        headerName: 'FRTs',
        flex: 3,
        renderCell: params => {
          const state = editingStates[params.row.id] || {};
          return state.isEditing ? (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <TokenInput
                tokens={state.tempDevIndexes || []}
                setTokens={tokens =>
                  updateEditingState(params.row.id, { tempDevIndexes: tokens })
                }
                placeholder="Enter FRT"
              />
            </Box>
          ) : (
            <TokenInput
              tokens={params.row.devIndexes || []}
              placeholder=""
              disabled={true}
            />
          );
        },
      },
    ];

    // Add Actions column only for MBF Admin
    if (user.user.is_admin && user.user.TL_name === 'MBF Admin') {
      baseColumns.push({
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const state = editingStates[params.row.id] || {};
          return state.isEditing ? (
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => handleSave(params.row.id)}
                color="primary"
                size="small"
                title="Save Changes"
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={() => handleCancel(params.row.id)}
                color="secondary"
                size="small"
                title="Cancel"
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : (
            <IconButton
              onClick={() => handleEdit(params.row.id)}
              color="primary"
              size="small"
              title="Edit Row"
            >
              <EditIcon />
            </IconButton>
          );
        },
      });
    }

    return baseColumns;
  }, [editingStates, user.user.is_admin, user.user.TL_name]);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            border: '1px solid #CCCCCC',
            borderRadius: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : devIndexData.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            border: '1px solid #CCCCCC',
            borderRadius: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            minHeight: '60vh',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No FRT Management entries found
          </Typography>
        </Box>
      ) : (
        <Box>
          {isMobile ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr', // Mobile → 1 card
                  sm: '1fr 1fr', // Tablet → 2 cards
                },
                gap: 2,
              }}
            >
              {devIndexData.map((row) => (
                <Paper
                  key={row.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#fff',
                    transition: '0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <Stack spacing={2}>

                    {/* TL Name */}
                    <Box display="flex" alignItems="center" gap="5px"> 
                      <Typography
                        variant="caption"
                        sx={{ color: '#6B7280', fontWeight: 600 }}
                      >
                        TL Name:
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <TokenInput
                          tokens={row.name ? [row.name] : []}
                          disabled
                        />
                      </Box>
                    </Box>

                    {/* LDAPs */}
                    <Box display="flex" alignItems="center" gap="5px">
                      <Typography
                        variant="caption"
                        sx={{ color: '#6B7280', fontWeight: 600 }}
                      >
                        LDAPs:
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <TokenInput
                          tokens={row.accepted_ldaps || []}
                          disabled
                        />
                      </Box>
                    </Box>

                    {/* FRTs */}
                    <Box display="flex" alignItems="center" gap="5px">
                      <Typography
                        variant="caption"
                        sx={{ color: '#6B7280', fontWeight: 600 }}
                      >
                        FRTs:
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <TokenInput tokens={row.devIndexes || []} disabled />
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Box>
          ) : (
            <DataGrid
              rows={devIndexData}
              columns={columns}
              getRowId={row => row.id}
              getRowHeight={() => 'auto'}
              autoHeight
              disableColumnMenu
              disableRowSelectionOnClickpagination
              pageSizeOptions={[7, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 7 } },
              }}
              paginationMode="client"
              slots={{
                pagination: CustomPagination,
              }}
              sx={{
                border: '1px solid #CCCCCC',
                borderRadius: '8px',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#F2F2F2',
                  borderRadius: '8px 8px 0px 0px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#F3F4F6',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #F1F1F1',
                },
                '& .MuiDataGrid-footerContainer': {
                  justifyContent: 'center',
                  alignItems: 'center',
                  '& .MuiTablePagination-root': {
                    textAlign: 'center',
                    '& .MuiButtonBase-root': {
                      color: '#3f3f3f',
                    },
                  },
                },
                '& .MuiPaginationItem-root': {
                  color: '#3f3f3f',
                },
                '& .MuiDataGrid-selectedRowCount': {
                  visibility: 'hidden',
                  width: 0,
                  padding: 0,
                  margin: 0,
                  flex: '0 !important',
                },
                '& .MuiDataGrid-footerContainer': {
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  justifyContent: 'center',
                  borderTop: '1px solid #F1F1F1',
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          zIndex: 99999,
        }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          zIndex: 99999,
        }}
      >
        <Alert
          onClose={() => setError('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FRTManagementContainer;
